import { useEffect, useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import type { Source } from '../deals/types';

type ElasticFetchProps = {
  query: object;
  campaignId?: number;
  timeout?: number;
};

type HitsType = {
  _source: Source;
  _id: string;
  _score: number;
  _type: string;
};

export type ResponseData = {
  hits?: {
    total: number;
    hits: HitsType[];
  };
  aggregations?: {
    stores?: {
      current?: {
        buckets: [];
        doc_count_error_upper_bound: number;
        sum_other_doc_count: number;
      };
      buckets: [];
      doc_count: number;
    };
    category?: {
      current: {
        buckets: [];
      };
    };
  };
};

type ResponseType = {
  data: ResponseData;
  fetching: boolean;
  error: string | null;
};

const defaultResponse: ResponseType = {
  data: {},
  fetching: true,
  error: null,
};

export const useElasticFetch = ({
  query,
  campaignId,
  timeout = 500,
}: ElasticFetchProps) => {
  const [response, setResponse] = useState<ResponseType>(defaultResponse);
  const [initFetched, setInitFetched] = useState(false);
  const [currentQuery, setCurrentQuery] = useState<object>();
  const { market } = useAppContext();

  useEffect(() => {
    if (JSON.stringify(query) === JSON.stringify(currentQuery)) {
      return;
    }

    setCurrentQuery(query);

    if (!initFetched) {
      apiFetch();
      setInitFetched(true);
      return;
    }

    const timer = setTimeout(() => {
      apiFetch();
    }, timeout);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [query]);

  const apiFetch = async () => {
    setResponse(defaultResponse);
    const searchUrl = `${market.campaignsBaseUrl}/campaign-${market.code}-${campaignId}-deals/_search`;
    const options = {
      body: JSON.stringify(query),
      method: 'POST',
    };

    try {
      const request = await fetch(searchUrl, options);
      const jsonResponse = await request.json();
      setResponse({ data: jsonResponse, fetching: false, error: null });
    } catch (e: any) {
      setResponse({ data: {}, fetching: false, error: e.toString() });
    }
  };

  return response;
};
